/**
 * Head data for the landing and the share view (spec §4.4, K-4): title, description, canonical, robots, Open
 * Graph, Twitter and the JSON-LD (K-5). Pure: no DOM, no React. Two consumers share it:
 *   - components/EventsHead.js renders it through the site's helmet shim at runtime;
 *   - scripts/events-route.js turns it into the site's postbuild ROUTES entry (`t`, `d`, `robots`, `j`).
 */
import { pageJsonLd, shareUrl, SITE_ORIGIN } from "./jsonld";

export const CANONICAL = `${SITE_ORIGIN}/events`;

/**
 * The launch flag (BEFORE-YOU-DEPLOY.md §1): one switch, three effects — `noindex` on /events and
 * /events/{slug}, whether event URLs enter sitemap.xml, and whether the Events link shows in the nav. Default
 * off, so the pages can ship to production and stay invisible until the owner releases them. It is read at
 * build time (the robots tag and the sitemap are baked), so flipping it is a value change plus a redeploy.
 * The unlisted rule and an ended event are independent of it and stay noindex either way.
 */
export const RELEASED = process.env.REACT_APP_EVENTS_LAUNCH === "true";
export const indexable = (robots) => (RELEASED ? robots : robots.replace(/^index/, "noindex"));

/** The DOM id of the JSON-LD script, baked (`jid`) and rendered at runtime alike, so one replaces the other. */
export const JSONLD_ID = "events-page";

export const LANDING = {
  title: "Events | KGOB CPAs & Advisors",
  description: "Free seminars, workshops, webinars and client evenings from KGOB CPAs & Advisors across the Carolinas, Pittsburgh and online. See all events and RSVP in under a minute.",
  ogTitle: "KGOB Events",
  ogDescription: "Good company. Better decisions. Free events across the Carolinas, Pittsburgh and online.",
  // The 1200x630 social card (what Facebook, LinkedIn and iMessage crop to); the hero itself is belmont-hero.webp/.jpg at 1200x600
  image: { url: `${SITE_ORIGIN}/events-assets/belmont-hero-og.jpg`, alt: "Coffee and conversation at a KGOB business breakfast" },
};

/**
 * The head model. `lead` is the leading event (share view) or null (landing); `shown` is the hero's
 * events-and-dates for JSON-LD; og:url is the page's own URL (with ?e= on a share view).
 */
export function buildHead({ lead = null, shown = [], siteOrigin = SITE_ORIGIN, now = new Date() } = {}) {
  const jsonLd = pageJsonLd(shown, siteOrigin, now);
  if (!lead) {
    return {
      title: LANDING.title, description: LANDING.description, canonical: CANONICAL, robots: indexable("index, follow"),
      og: { title: LANDING.ogTitle, description: LANDING.ogDescription, url: `${siteOrigin}/events`, type: "website", siteName: "KGOB CPAs & Advisors", image: LANDING.image },
      twitter: { card: "summary_large_image", title: LANDING.ogTitle, description: LANDING.ogDescription, image: LANDING.image.url },
      jsonLd,
    };
  }
  const img = lead.images?.og ?? lead.images?.hero ?? null;
  const image = img ? { url: img.url, alt: img.alt } : LANDING.image;
  const noindex = lead.visibility === "unlisted" || lead.seo?.noindex === true;
  return {
    title: lead.seo?.title || lead.title,
    description: lead.seo?.description || lead.summary,
    canonical: CANONICAL,
    robots: noindex ? "noindex, follow" : indexable("index, follow"),
    og: { title: lead.title, description: lead.summary, url: shareUrl(siteOrigin, lead.slug), type: "website", siteName: "KGOB CPAs & Advisors", image },
    twitter: { card: "summary_large_image", title: lead.title, description: lead.summary, image: image.url },
    jsonLd,
  };
}

/** The meta and link tags as [tagName, attributes] pairs (the title and the JSON-LD script are separate). */
export function headTags(head) {
  const m = (attrs) => ["meta", attrs];
  return [
    m({ name: "description", content: head.description }),
    ["link", { rel: "canonical", href: head.canonical }],
    m({ name: "robots", content: head.robots }),
    m({ property: "og:type", content: head.og.type }),
    m({ property: "og:site_name", content: head.og.siteName }),
    m({ property: "og:title", content: head.og.title }),
    m({ property: "og:description", content: head.og.description }),
    m({ property: "og:url", content: head.og.url }),
    m({ property: "og:image", content: head.og.image.url }),
    m({ property: "og:image:alt", content: head.og.image.alt }),
    m({ name: "twitter:card", content: head.twitter.card }),
    m({ name: "twitter:title", content: head.twitter.title }),
    m({ name: "twitter:description", content: head.twitter.description }),
    m({ name: "twitter:image", content: head.twitter.image }),
  ];
}
